/**
 * `parcel-locale-codec.ts` — locale-unit ⇄ canonical-unit codecs for the
 * parcel dimension inputs.
 *
 * # The round-trip problem
 *
 * Parcel storage is canonical and INTEGER: `listings.parcel_weight_grams` and
 * `parcel_{length,width,height}_mm`, and `create_listing_with_auction` casts
 * with `(p_listing->>'parcel_length_mm')::int`. A US seller types inches. The
 * two grids do not line up — 1 in = 25.4 mm — so the naive display conversion
 * is lossy in a way the seller can see:
 *
 *     type "6" in  →  6 × 25.4 = 152.4  →  round → 152 mm   (stored)
 *     display      →  152 / 25.4 = 5.9842  →  round(2dp) → "5.98"
 *
 * The seller typed 6 and the field said 5.98. Nothing was actually wrong with
 * the stored value — 152 mm IS six inches to the nearest millimetre — the
 * DISPLAY just picked a needlessly precise way to say it.
 *
 * # The fix: shortest round-trip-stable representation
 *
 * `format` does not round to a fixed precision. It searches decimal places
 * `0, 1, … maxDecimals` and returns the FIRST representation that converts
 * back to the exact canonical value already stored:
 *
 *     152 mm → "6"     because 6 × 25.4 → 152 ✓          (0 dp wins)
 *     153 mm → "6.02"  because 6 → 152 ✗, 6.0 → 152 ✗    (2 dp needed)
 *     340 g  → "12"    because 12 × 28.3495 → 340 ✓
 *
 * So `parse(format(v)) === v` holds — the contract `NumericFieldCodec`
 * requires — while the seller sees the roundest true number.
 *
 * This is display-only. Canonical values, the DB columns, and the
 * `MIN_PARCEL_*` floors in `@kraahl/contract` are untouched. Note the floors
 * are not on the inch grid either (`MIN_PARCEL_LENGTH_MM = 153` = 6.02 in),
 * so a flat `6` still reads as below minimum — correctly, and now visibly:
 * the error names 6.02 in and typing 6.02 clears it.
 *
 * # Unit precision
 *
 * `maxDecimals` per unit is "enough decimals to address one canonical step":
 * 1 mm ≈ 0.039 in → 2 dp; 1 mm = 0.1 cm → 1 dp; 1 g ≈ 0.035 oz → 2 dp;
 * 1 g = 0.001 kg → 3 dp. More would only let the seller type digits that
 * cannot survive the integer store.
 *
 * @see lib/shipping/normalize-parcel.ts (`toGrams` / `toMm` — the conversion SoT)
 * @see lib/forms/numeric-draft.ts (`NumericFieldCodec` contract)
 * @see components/shipping/ShippingModeToggleAndParcelForm.tsx (consumer)
 */

import {
  formatNumericDraft,
  parseNumericDraft,
  type NumericFieldCodec,
} from "@/lib/forms/numeric-draft";
import {
  gramsPerMassUnit,
  mmPerDistanceUnit,
  toGrams,
  toMm,
} from "@/lib/shipping/normalize-parcel";

/** Mass unit a seller types in. Narrower than `MassUnit` — locale picks one of two. */
export type LocaleMassUnit = "oz" | "kg";
/** Distance unit a seller types in. Narrower than `DistanceUnit` — locale picks one of two. */
export type LocaleDistanceUnit = "in" | "cm";

export interface LocaleParcelUnits {
  mass: LocaleMassUnit;
  distance: LocaleDistanceUnit;
}

const MASS_MAX_DECIMALS: Readonly<Record<LocaleMassUnit, number>> = {
  oz: 2,
  kg: 3,
};

const DISTANCE_MAX_DECIMALS: Readonly<Record<LocaleDistanceUnit, number>> = {
  in: 2,
  cm: 1,
};

/**
 * Locale unit derivation from the seller's ISO-3166 alpha-2 country. US
 * sellers type imperial (oz + in); everyone else metric (kg + cm).
 *
 * Single source of truth for the seller-INPUT side of this rule, per PLAN §A
 * "Parcel units" (locked 2026-06-22). The viewer-OUTPUT mirror lives in
 * `lib/shipping/format-parcel.ts::localeUnitsForViewer` — same policy, and a
 * change to one must be mirrored in the other until they are merged.
 */
export function localeUnitsForSeller(country: string): LocaleParcelUnits {
  const upper = (country || "").toUpperCase();
  return upper === "US"
    ? { mass: "oz", distance: "in" }
    : { mass: "kg", distance: "cm" };
}

/**
 * Shortest decimal representation of `canonical` in locale units that
 * `toCanonical` maps back to `canonical` exactly.
 *
 * Falls through to full `maxDecimals` precision when no shorter form survives
 * the trip — that value still round-trips in every case reachable here, since
 * `maxDecimals` addresses one canonical step by construction (see file
 * header), but the fallback keeps the function total rather than relying on
 * that argument holding for a future unit.
 */
function shortestStableDraft(
  canonical: number,
  toLocale: (canonical: number) => number,
  toCanonical: (locale: number) => number,
  maxDecimals: number,
): string {
  const exact = toLocale(canonical);
  for (let dp = 0; dp <= maxDecimals; dp++) {
    const candidate = Number(exact.toFixed(dp));
    if (toCanonical(candidate) === canonical) {
      return formatNumericDraft(candidate, maxDecimals);
    }
  }
  return formatNumericDraft(exact, maxDecimals);
}

/**
 * Codec for the weight input: draft is in the seller's locale mass unit, the
 * stored value is canonical grams.
 */
export function massCodecForUnit(unit: LocaleMassUnit): NumericFieldCodec {
  const maxDecimals = MASS_MAX_DECIMALS[unit];
  const gramsPerUnit = gramsPerMassUnit(unit);
  return {
    maxDecimals,
    parse: (draft) => {
      const locale = parseNumericDraft(draft);
      return locale === undefined ? undefined : toGrams(locale, unit);
    },
    format: (grams) =>
      shortestStableDraft(
        grams,
        (g) => g / gramsPerUnit,
        (locale) => toGrams(locale, unit),
        maxDecimals,
      ),
  };
}

/**
 * Codec for a length / width / height input: draft is in the seller's locale
 * distance unit, the stored value is canonical millimetres.
 */
export function distanceCodecForUnit(
  unit: LocaleDistanceUnit,
): NumericFieldCodec {
  const maxDecimals = DISTANCE_MAX_DECIMALS[unit];
  const mmPerUnit = mmPerDistanceUnit(unit);
  return {
    maxDecimals,
    parse: (draft) => {
      const locale = parseNumericDraft(draft);
      return locale === undefined ? undefined : toMm(locale, unit);
    },
    format: (mm) =>
      shortestStableDraft(
        mm,
        (value) => value / mmPerUnit,
        (locale) => toMm(locale, unit),
        maxDecimals,
      ),
  };
}
